import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import api from 'api/Assessor/evento';
import { useHistory, useParams } from 'react-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { IReducer } from 'store/Reducer';
import {
  assembleiasAssessorActionsLoadDuvidas,
  assembleiasAssessorLoadEvento,
  ThunkDuvidasReturn,
} from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorEvent } from 'store/modules/AssembleiasAssessorReducer';
import { PerguntaResponse, ListaPerguntaResponse } from 'api/Assessor/duvidas';
import { ErrorAPI } from 'api/Assessor/methods';
import ErrorHandler from 'utils/ErrorHandler';
import { Grid, LinearProgress } from '@material-ui/core';
import PrivateContainer from 'components/PrivateContainer/PrivateContainer';
import Context, { UserAuth } from './Context';
import Header from './Header';
import ListaConversas from './Conversas';
import Chat from './Chat';
import useStyle from './style';
import useCountdown from 'hooks/useCountdown';
import LayoutContext from 'components/Layout/LayoutContext';
import { ITopBarMenuItem } from 'components/TopBar/TopBarMenuItem';
import { getAcessoRapido } from 'utils/TopBarMenuUtils';
import { TopBarMenuAcessoRapido } from 'enuns/TopBarMenuAcessoRapido';
import { useTranslation } from 'react-i18next';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { getUniqueKey } from 'utils/KeyUtils';
import IncluirPerguntaChatModal, { IIncluirPerguntaChatModal } from 'components/IncluirPerguntaChatModal/IncluirPerguntaChatModal';
import AxiosErrorHandler from 'utils/AxiosErrorHandler';
import { AssessorRoutePath } from 'library/Routes';

interface PathParams {
  idEvento: string;
}

const RELOAD_INTERVAL = 60;

const mapStateToProps = (state: IReducer) => ({
  duvidas: state.assembleiasAssessor.duvidas,
  evento: state.assembleiasAssessor.evento,
  loadingEvento: state.assembleiasAssessor.loadingEvento,
  auth: state.auth.usuario?.perfil.assessorRoute.duvidasSugestoes,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      assembleiasAssessorActionsLoadDuvidas,
    },
    dispatch
  );

type AssessorDuvidasAction = (
  idEvento: number,
  successCb?: (duvidas: ListaPerguntaResponse) => void,
  errorCb?: (error: ErrorAPI) => void
) => ThunkDuvidasReturn;

interface Props {
  loadingEvento: boolean;
  evento: IAssembleiasAssessorEvent | null;
  duvidas: ListaPerguntaResponse;
  assembleiasAssessorActionsLoadDuvidas: AssessorDuvidasAction;
  auth: UserAuth | undefined;
}

function MensagensAssessor({
  assembleiasAssessorActionsLoadDuvidas,
  loadingEvento,
  evento,
  duvidas,
  auth,
}: Props) {
  const classes = useStyle();
  const { idEvento } = useParams<PathParams>();
  const [conversaAtual, setConversa] = useState<PerguntaResponse | null>(null);
  const [termoPesquisa, pesquisar] = useState<string>();
  const [countdownEnded, setCountdownEnded] = useState<boolean>(false);
  const countDown = useCountdown(RELOAD_INTERVAL, undefined, undefined, () => setCountdownEnded(true), true);
  const { usuario } = useSelector((state: IReducer) => state.auth);
  const { loadingDuvidas } = useSelector((state: IReducer) => state.assembleiasAssessor);
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const { setPageMenuItems } = useContext(LayoutContext);
  const [habilitandoDesabilitando, setHabilitandoDesabilitando] = useState<boolean>(false);
  const history = useHistory();

  const irParaConversa = useCallback(
    (cpfUsuario: string) => {
      const conversa = duvidas.find(
        usuario => usuario.cpfUsuario === cpfUsuario
      );
      setConversa(conversa || null);
    },
    [duvidas]
  );

  const atualizarEvento = useCallback(() => {
    dispatch(
      assembleiasAssessorLoadEvento(
        parseInt(idEvento),
        undefined,
        error => {
          AxiosErrorHandler(error);
          if (error.response?.status === 417) {
            history.push(AssessorRoutePath);
          }
        }
      )
    )
  }, [idEvento, history, dispatch]);

  const atualizarMensagens = useCallback(
    (successCb?: (conversas: ListaPerguntaResponse) => void) => {
      assembleiasAssessorActionsLoadDuvidas(
        Number(idEvento),
        (conversas: ListaPerguntaResponse) => {
          countDown.toRestart();
          if (typeof successCb === 'function') {
            successCb(conversas);
          }
        },
        ErrorHandler
      );
    },
    [assembleiasAssessorActionsLoadDuvidas, idEvento, countDown]
  );

  useEffect(() => atualizarEvento(), [atualizarEvento]);

  useEffect(() => {
    if (countdownEnded) {
      setCountdownEnded(false);
      countDown.cancel();
      atualizarMensagens();
    }
    // eslint-disable-next-line
  }, [countdownEnded, setCountdownEnded, atualizarMensagens])

  useEffect(() => {
    atualizarMensagens();
    // eslint-disable-next-line
  }, [idEvento])

  useEffect(() => {
    countDown.cancel();
    return () => {
      countDown.cancel();
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => pesquisar(''), [evento]);

  useEffect(() => {
    if (conversaAtual) {
      const conversa = duvidas.find(
        usuario => usuario.cpfUsuario === conversaAtual.cpfUsuario
      );
      setConversa(conversa || null);
    }
  }, [duvidas, conversaAtual]);

  const incluirPergunta = useCallback(() => {
    if (!evento) return;
    const modalKey = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        IncluirPerguntaChatModal,
        {
          genericProps: {
            key: modalKey,
            idEvento: evento.id,
            callbackSuccess: () => atualizarMensagens(),
          },
        } as IIncluirPerguntaChatModal,
        modalKey
      )
    );
  }, [dispatch, evento, atualizarMensagens]);

  const habilitarForum = useCallback(async () => {
    if (!evento) return;
    setHabilitandoDesabilitando(true);
    const { fail, response } = await api.mensagens.liberar(evento.id);
    setHabilitandoDesabilitando(false);
    if (fail) {
      ErrorHandler(fail);
    }
    if (response && response.status === 200) {
      atualizarEvento();
    }
  }, [evento, atualizarEvento]);

  const desabilitarForum = useCallback(async () => {
    if (!evento) return;
    setHabilitandoDesabilitando(true);
    const { fail, response } = await api.mensagens.bloquear(evento.id);
    setHabilitandoDesabilitando(false);
    if (fail) {
      ErrorHandler(fail);
    }
    if (response && response.status === 200) {
      atualizarEvento();
    }
  }, [evento, atualizarEvento]);

  const subMenu = useMemo<ITopBarMenuItem[]>(() => {
    if (usuario) {
      const menus: ITopBarMenuItem[] = getAcessoRapido(t, usuario.perfil, TopBarMenuAcessoRapido.Mensagens, idEvento);

      menus.push({
        label: t(`SubMenus.incluirMensagem`),
        disabled: !evento || loadingEvento,
        onClick: incluirPergunta
      });

      if (evento) {
        menus.push({
          label: t(`SubMenus.${evento.statusChat ? 'desabilitar' : 'habilitar'}Mensagens`),
          disabled: loadingEvento || habilitandoDesabilitando,
          onClick: evento.statusChat ? desabilitarForum : habilitarForum
        });
      }

      menus.push({
        label: t(`SubMenus.atualizarMensagens`),
        disabled: !evento || loadingEvento || loadingDuvidas,
        onClick: () => atualizarMensagens()
      })

      return menus;
    }

    return [];

    // eslint-disable-next-line
  }, [t, usuario, idEvento, loadingEvento, evento, habilitandoDesabilitando, loadingDuvidas])

  useEffect(() => {
    setPageMenuItems(subMenu);
    return () => setPageMenuItems();
  }, [subMenu, setPageMenuItems]);

  return (
    <Context.Provider
      value={{
        conversas: duvidas,
        evento,
        conversaAtual,
        irParaConversa,
        atualizarMensagens,
        auth,
        termoPesquisa,
        pesquisar,
        atualizarEvento,
        habilitandoDesabilitando,
      }}
    >
      <PrivateContainer noPadding noScroll>
        <Grid
          container
          className={classes.root}
          component="article"
          spacing={0}
        >
          <Header />
          <ListaConversas />
          <Chat />
        </Grid>
      </PrivateContainer>
      {(loadingEvento || loadingDuvidas) && (
        <LinearProgress className={classes.loading} />
      )}
    </Context.Provider>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(MensagensAssessor);
